/**
 * §2.3 `hours_json` editor.
 *
 * Every day is present and an empty array means closed — the schema requires all
 * seven keys, so "closed" must be an explicit empty list rather than a missing
 * one. A day may carry more than one range because the lunch break is the norm
 * here, which is exactly why this is not a simple from/to pair.
 */
import type { components } from '@navoiy/api-types';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { Button, TextInput } from '../ui/index.jsx';

type WeeklyHours = components['schemas']['WeeklyHours'];
type TimeRange = components['schemas']['TimeRange'];

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type Day = (typeof DAYS)[number];

export const EMPTY_HOURS: WeeklyHours = {
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
};

export function HoursEditor({
  value,
  onChange,
}: {
  value: WeeklyHours;
  onChange: (next: WeeklyHours) => void;
}): React.JSX.Element {
  const { t } = useI18n();

  function setDay(day: Day, ranges: TimeRange[]): void {
    onChange({ ...value, [day]: ranges });
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs font-semibold uppercase tracking-label text-ink-500">
        {t('admin.directory.hours')}
      </legend>
      {DAYS.map((day) => {
        const ranges = value[day] ?? [];
        return (
          <div key={day} className="flex flex-wrap items-center gap-2 border-b-1 border-line-100 py-2">
            <span className="w-28 text-sm font-medium text-ink-700">
              {t(`admin.directory.day.${day}`)}
            </span>

            {ranges.length === 0 ? (
              <span className="text-xs text-ink-400">{t('admin.directory.closedDay')}</span>
            ) : null}

            {ranges.map((range, index) => (
              <span key={index} className="flex items-center gap-1">
                <TextInput
                  type="time"
                  className="w-28"
                  value={range.from}
                  onChange={(event) => {
                    const next = [...ranges];
                    next[index] = { ...range, from: event.target.value };
                    setDay(day, next);
                  }}
                />
                <span className="text-ink-400">–</span>
                <TextInput
                  type="time"
                  className="w-28"
                  value={range.to}
                  onChange={(event) => {
                    const next = [...ranges];
                    next[index] = { ...range, to: event.target.value };
                    setDay(day, next);
                  }}
                />
                <Button
                  tone="ghost"
                  type="button"
                  onClick={() => setDay(day, ranges.filter((_, i) => i !== index))}
                >
                  ✕
                </Button>
              </span>
            ))}

            <Button
              tone="ghost"
              type="button"
              onClick={() => setDay(day, [...ranges, { from: '09:00', to: '18:00' }])}
            >
              + {t('admin.directory.addRange')}
            </Button>
          </div>
        );
      })}
    </fieldset>
  );
}
